import React from 'react'
import { Paper, Typography, Button, Box, CircularProgress } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { PortalLayout } from '@/apps/legacy/components/portal/PortalLayout'
import { PortalNavigationButton } from '@/apps/legacy/components/portal/NavigationButtons'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { useToast } from '@/shared/contexts/ToastContext'
import { NSIPS_ERRORS } from '@/apps/legacy/constants/nsips'
import {
  useNSIPSFormValidation,
  useNSIPSForm,
  useExportNSIPSMutation,
  PatientInfoSection,
  PrescriptionSection,
} from '@/features/internal'

export default function NSIPSExportPage() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const exportMutation = useExportNSIPSMutation()
  const { validateForm } = useNSIPSFormValidation()
  const {
    formData,
    errors,
    setErrors,
    handlePatientFieldChange,
    handlePrescriptionFieldChange,
    handleDrugFieldChange,
    handleDrugSelect,
    addPrescription,
    removePrescription,
    addDrug,
    removeDrug,
  } = useNSIPSForm()

  const handleValidateForm = (): boolean => {
    const newErrors = validateForm(formData)
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleExport = async () => {
    if (!handleValidateForm()) {
      showToast(NSIPS_ERRORS.VALIDATION_ERROR, 'error')
      return
    }

    await exportMutation.mutateAsync(formData)
  }

  const loading = exportMutation.isPending

  const handleCancel = () => {
    navigate('/portal')
  }

  return (
    <PortalLayout>
      <Typography
        variant="h5"
        sx={{
          color: colorTokens.text.primary,
          mb: 3,
          mt: 2,
        }}
      >
        NSIPSデータエクスポート
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          ■ 患者情報
        </Typography>
        <PatientInfoSection
          formData={formData}
          errors={errors}
          onChange={handlePatientFieldChange}
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          ■ 処方内容
        </Typography>

        {formData.prescriptions.map((prescription, rpIndex) => (
          <PrescriptionSection
            key={rpIndex}
            prescription={prescription}
            rpIndex={rpIndex}
            errors={errors}
            canDelete={formData.prescriptions.length > 1}
            onUsageChange={handlePrescriptionFieldChange(rpIndex, 'usage')}
            onDaysChange={handlePrescriptionFieldChange(rpIndex, 'days')}
            onDrugFieldChange={(drugIndex, field) =>
              handleDrugFieldChange(rpIndex, drugIndex, field)
            }
            onDrugSelect={(drugIndex) => handleDrugSelect(rpIndex, drugIndex)}
            onAddDrug={() => addDrug(rpIndex)}
            onRemoveDrug={(drugIndex) => removeDrug(rpIndex, drugIndex)}
            onRemovePrescription={() => removePrescription(rpIndex)}
          />
        ))}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addPrescription}
          sx={{
            mt: 2,
            color: colorTokens.blue[600],
            borderColor: colorTokens.blue[600],
            backgroundColor: colorTokens.action.primaryBgLight,
            '&:hover': {
              borderColor: colorTokens.blue[700],
              backgroundColor: colorTokens.action.primaryBgHover,
            },
          }}
        >
          RPを追加
        </Button>
      </Paper>

      <Box display="flex" justifyContent="space-between" sx={{ mt: 4 }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={loading}
          sx={{
            color: colorTokens.grey[500],
            borderColor: colorTokens.border.light,
            backgroundColor: colorTokens.action.secondaryBgLight,
            '&:hover': {
              borderColor: colorTokens.grey[500],
              backgroundColor: colorTokens.action.secondaryBgHover,
            },
            '&:disabled': {
              color: colorTokens.text.disabled,
              borderColor: colorTokens.border.light,
              backgroundColor: 'transparent',
            },
          }}
        >
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={loading}
          sx={{
            backgroundColor: colorTokens.blue[600],
            color: colorTokens.background.paper,
            '&:hover': {
              backgroundColor: colorTokens.blue[700],
            },
            '&:disabled': {
              backgroundColor: colorTokens.grey[300],
              color: colorTokens.text.disabled,
            },
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: 'inherit' }} />
          ) : (
            'エクスポート実行'
          )}
        </Button>
      </Box>

      <PortalNavigationButton
        variant="back"
        onClick={() => navigate('/portal')}
      />
    </PortalLayout>
  )
}
